import { ISLAND_SLOTS, type IslandSlot } from './islands.ts';
import type {
  BuildTask,
  CharacterEntry,
  SceneSet,
  Sentence,
  Word,
  WordCategory,
} from '@ezishizi/content-schema';

export type MasterySkill =
  'meaning' | 'listen' | 'speak' | 'read' | 'pinyin' | 'sentence' | 'write_guided' | 'write_memory';

export const MASTERY_SKILLS: readonly MasterySkill[] = [
  'meaning',
  'listen',
  'speak',
  'read',
  'pinyin',
  'sentence',
  'write_guided',
  'write_memory',
];

export interface MasteryState {
  character: string;
  skill: MasterySkill;
  score: number; // 0..100
  attempts: number;
  correct: number;
  lastAt: string | null; // ISO
  dueAt: string | null; // ISO
}

export type ExerciseType =
  | 'introduce'
  | 'audio_to_picture'
  | 'picture_to_hanzi'
  | 'audio_to_hanzi'
  | 'hanzi_to_picture'
  | 'speak'
  | 'trace'
  | 'sentence_fill'
  | 'pinyin_to_hanzi'
  | 'tone'
  | 'find_all'
  | 'listen_catch'
  | 'match_pairs'
  /** Three pictures introduced side by side, each spoken on a tap (the trio Listen intro). */
  | 'introduce_trio'
  /** Boat Forks: at each fork, hear the word and tap the right signpost. */
  | 'listen_forks'
  /** Feed Shizi: hear what he wants and give it to him from the tray. */
  | 'listen_feed'
  /** Which scene: hear a sentence, tap the scene it describes. */
  | 'listen_scene'
  /** Listen and build: hear an instruction, move the object to the place it names. */
  | 'listen_build'
  /** Bubble Lagoon: pop the bubbles carrying the called word. */
  | 'listen_bubbles'
  /** Shell sort by sound: hear a shell, sort it into the basket of the word it says. */
  | 'listen_sort'
  /** Hear the character, pick its pinyin (nothing else on screen). */
  /** The character with one stroke missing: draw that stroke. */
  | 'stroke_missing'
  /** The strokes as tiles: tap them in writing order. */
  | 'stroke_order'
  /** Tone painter: hear a syllable, draw its tone contour (or tap one of four arrows). */
  | 'tone_draw'
  /** P3+ reading practice (`docs/missions/spec.md`): a literal/inference MCQ over a passage. */
  | 'practice_mcq'
  /** P3+ reading practice: a word-choice cloze, standalone or anchored in a passage sentence. */
  | 'practice_cloze'
  /** P4+ reading practice: put 3-5 sentences/fragments of a short paragraph in order. */
  | 'practice_ordering'
  /** P4+ reading practice: hear a clip, then answer an MCQ (the script is hidden until after). */
  | 'practice_listening_mcq'
  /** P4+ reading practice: read one sentence at a time aloud, scored by the speech pipeline. */
  | 'practice_read_aloud';

/** `flash`: the model shows for a moment, then the child writes from memory. */
export type TraceMode = 'guided' | 'faded' | 'memory' | 'flash';

/** What an introduce card shows on its own: one dimension of the word, or everything. */
export type IntroduceShow = 'picture' | 'hanzi' | 'both' | 'pinyin';

/**
 * A frame is a way of presenting a question that already exists: the runner records what the
 * plain question would record; only the screen differs. The engine attaches the data the frame
 * needs (a plank order, the parts of a character, the baskets) so a retry looks the same.
 */
export type FrameData =
  /** Trace from memory; each correct stroke uncovers a slice of the picture. */
  | { frame: 'reveal' }
  /** Tone question answered by sliding along a contour arrow. */
  | { frame: 'tone_slide' }
  /** Sentence fill answered by putting every plank in order. `chunks` groups content-token
   * indices into planks (two planks early in P1, one token per plank later); `order` lists
   * chunk indices in the scrambled order they are offered. */
  | { frame: 'bridge'; chunks: number[][]; order: number[] }
  /** Picture or audio to hanzi answered by assembling the character from its parts. */
  | { frame: 'builder'; ids: string; parts: string[]; distractor: string | null }
  /** Hanzi to picture answered by flicking the card into the basket of its category. */
  | {
      frame: 'sort_meaning';
      left: WordCategory;
      right: WordCategory;
      leftWord: Word;
      rightWord: Word;
      /** The round's first card: Shizi names both baskets before it. */
      intro: boolean;
      answer: 'left' | 'right';
    }
  /** Audio to hanzi on a numeral or a measure word, with the objects to count on screen. */
  /** `hear`: the count is heard (三条鱼) and the child taps the plate with that many; `plates`
   * lists the counts on offer, in the order of `choices` (numeral characters). */
  | {
      frame: 'count';
      n: number;
      noun: Word;
      measure: string;
      variant: 'numeral' | 'measure' | 'hear';
      plates?: number[];
    };

export type FrameId = FrameData['frame'];

interface Base {
  id: string;
  entry: CharacterEntry;
  word: Word;
  skill: MasterySkill;
  /** Present this question as a game instead of its plain screen (see FrameData). */
  frame?: FrameData;
  /** An instruction id Shizi speaks on a short card before this question ("Now the character"),
   * marking a stage change inside a recipe. */
  beat?: string;
}

/** One fork of Boat Forks or one feed of Feed Shizi: a prompt and its choices. */
export interface ChoiceRound {
  answerId: string;
  choices: Word[];
  /** Show the word's hanzi on the signs instead of pictures (a read-by-ear round). */
  hanzi: boolean;
  /** Feed Shizi from the third feed: the media id of a sentence with the word, spoken instead
   * of the bare word ("我有鱼"), when the set has one. */
  phrase?: string | null;
}

export type Question =
  | (Base & { type: 'introduce'; sentence: Sentence | null; show?: IntroduceShow })
  /** `fixedOrder`: the tiles keep the order given and the runner keeps their positions between
   * consecutive questions with the same choices (the trio Listen round). */
  | (Base & {
      type: 'audio_to_picture';
      choices: Word[];
      answerId: string;
      fixedOrder?: boolean;
      /** Word ids whose tile shows hanzi instead of the picture (Three at a time's last asks). */
      hanziIds?: string[];
    })
  | (Base & { type: 'hanzi_to_picture'; choices: Word[]; answerId: string })
  | (Base & { type: 'picture_to_hanzi'; choices: Word[]; answerId: string })
  | (Base & { type: 'audio_to_hanzi'; choices: string[]; answer: string })
  | (Base & { type: 'speak' })
  | (Base & { type: 'trace'; mode: TraceMode })
  | (Base & {
      type: 'sentence_fill';
      sentence: Sentence;
      blankIdx: number;
      choices: Word[];
      answerId: string;
    })
  /** Read the pinyin (the target syllable inside the word's pinyin), tap the matching hanzi. */
  | (Base & {
      type: 'pinyin_to_hanzi';
      /** The word's syllables, with the index of the one being asked. */
      syllables: string[];
      targetIdx: number;
      choices: string[];
      answer: string;
    })
  /** Hear/see the character, pick its syllable with the right tone mark. */
  | (Base & { type: 'tone'; choices: string[]; answer: string })
  /** A grid of look-alike characters with the target hidden `targetCount` times: tap every one. */
  /** `prompt`: how the target is shown: its hanzi, its hanzi for two seconds then hidden
   * (recall), or its pinyin only (`pinyin`). */
  | (Base & {
      type: 'find_all';
      tiles: string[];
      answer: string;
      targetCount: number;
      prompt: 'hanzi' | 'hidden' | 'pinyin';
      pinyin: string | null;
    })
  /** Words play one after another; tap only when the target word is heard. */
  | (Base & { type: 'listen_catch'; sequence: Word[]; answerId: string })
  /** Pair each picture with its word. `pairs` is the picture column's order, `hanziOrder` the
   * word ids in the hanzi column's order, both fixed by the engine so a retry looks the same. */
  | (Base & { type: 'match_pairs'; pairs: Word[]; hanziOrder: string[] })
  | (Base & { type: 'introduce_trio'; words: Word[] })
  /** Boat Forks: one question per fork. `forks` is the whole river (for the boat's position),
   * `step` the fork this question asks; `entry`/`word` are the fork's target. */
  | (Base & { type: 'listen_forks'; forks: ChoiceRound[]; step: number })
  /** Feed Shizi: one question per feed, likewise. */
  | (Base & { type: 'listen_feed'; rounds: ChoiceRound[]; step: number })
  | (Base & { type: 'listen_scene'; sentence: Sentence; set: SceneSet })
  /** `cue`: the round's first task, which also speaks the English cue before its instruction. */
  | (Base & { type: 'listen_build'; task: BuildTask; objects: Word[]; cue: boolean })
  /** Bubble Lagoon: one question per call. `calls` is the whole round (the net shows the
   * earlier catches), `step` the call this question asks. Exactly one bubble to pop per call:
   * asking for two sent the child after the same word twice running, and the second hunt was
   * silent, because the word is spoken once, when the question mounts. */
  | (Base & {
      type: 'listen_bubbles';
      words: Word[];
      calls: Array<{ wordId: string }>;
      step: number;
      /** Bubbles show hanzi instead of pictures. */
      showHanzi: boolean;
      /** Other pictures from the lesson that float about but are never called. */
      decoys: Word[];
    })
  /** Shell sort: one question per shell. `shells` is the whole beach in the order they wash
   * up (the baskets show the earlier ones), `step` the shell this question asks. Four baskets
   * where the lesson can fill them, not two: with two, half the shells are right by coin toss
   * and the sound contrast never has to be heard. `baskets[0]` and `[1]` are the pair the
   * `contrast` describes; the rest are the lesson filling the beach out. */
  | (Base & {
      type: 'listen_sort';
      baskets: Word[];
      shells: string[];
      contrast: string;
      step: number;
    })
  | (Base & { type: 'stroke_missing'; strokeIdx: number; mode: 'guided' | 'memory' })
  | (Base & { type: 'stroke_order'; strokeCount: number })
  /** `mode`: arrows to tap (lesson 1), draw over the shown contour (lesson 2), draw blind. */
  | (Base & { type: 'tone_draw'; syllable: string; tone: 1 | 2 | 3 | 4; mode: ToneDrawMode });

export type ToneDrawMode = 'arrows' | 'draw_shown' | 'draw';

/**
 * The games a word gets on the word-first adventure islands, in order: meet it, hear it, find
 * it, say and use it, and (write targets only) write it. Each is a short runner session.
 */
export type WordGame = 'meet' | 'listen' | 'find' | 'use' | 'write';
export const WORD_GAMES: readonly WordGame[] = ['meet', 'listen', 'find', 'use', 'write'];

export type Module =
  | 'learn'
  | 'listen'
  | 'speak'
  | 'read'
  | 'write'
  | 'pinyin'
  | 'practice'
  /** P3+ reading practice (`docs/missions/spec.md`): a lesson-scoped passage + questions,
   * distinct from `practice` (the P1–P2 "Play" stop's module) so progress keys never collide. */
  | 'reading_practice'
  | 'test'
  | 'review'
  | 'mistakes'
  | 'story'
  /** The end-of-island reward on the adventure path: a mixed round in a game frame. */
  | 'game'
  /** One stop of a games island (`islandModule`). The module is the *slot*, never the format, so
   * a stop the child finished stays finished when the format changes — a new lesson band opens
   * one, or content adds another. The format travels in the route's `game` search param. */
  | 'island_meet'
  | 'island_listen'
  | 'island_read'
  | 'island_pinyin'
  | 'island_write'
  | 'island_use'
  /** One word's games on the word-first islands (`wordModule`); the session also records the
   * character, since the set alone does not identify the activity. */
  | 'word_meet'
  | 'word_listen'
  | 'word_find'
  | 'word_use'
  | 'word_write'
  /** The daily challenge (`dailyChallenge.ts`): a mixed-pool test over every character the child
   * has attempted at least once, run outside the adventure path (`lessonId: CHALLENGE_LESSON_ID`). */
  | 'challenge';

export const wordModule = (game: WordGame): Module => `word_${game}`;

export const islandModule = (slot: IslandSlot): Module => `island_${slot}`;

/** The island slot a module is a stop for, or null for every other module. */
export function islandSlotOfModule(module: string): IslandSlot | null {
  if (!module.startsWith('island_')) return null;
  const slot = module.slice('island_'.length) as IslandSlot;
  return ISLAND_SLOTS.includes(slot) ? slot : null;
}

/** The word game a module runs, or null for the set/lesson modules. */
export function gameOfModule(module: string): WordGame | null {
  if (!module.startsWith('word_')) return null;
  const game = module.slice('word_'.length) as WordGame;
  return WORD_GAMES.includes(game) ? game : null;
}

export interface AttemptResult {
  questionId: string;
  type: ExerciseType;
  character: string;
  skill: MasterySkill;
  correct: boolean;
  retried: boolean;
  latencyMs?: number;
  /** Set on `type: 'speak'` results the Worker persisted (see workers/api's speech_attempts):
   * lets the result screen offer the parent a fair/unfair label for that attempt. */
  speechAttemptId?: string;
  /** The vendor-normalised verdict for `speechAttemptId`, so the parent's "Unfair" tap can be
   * turned into 'harsh' or 'lenient' without asking them to pick. */
  speechOutcome?: 'pass' | 'retry' | 'wrong' | 'no_speech';
}
